import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "context/AuthContext";
import {
  getCheckoutCampaignsCache,
  syncCheckoutCampaigns,
  type Campaign,
} from "api/order-entry.api";

export default function CheckoutCampaignsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super-admin";
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data, isLoading, error } = useQuery({
    queryKey: ["checkout-campaigns-cache"],
    queryFn: () => getCheckoutCampaignsCache().then((r) => r.data),
    enabled: isAdmin,
  });

  const syncMutation = useMutation({
    mutationFn: () => syncCheckoutCampaigns(),
    onSuccess: (res) => {
      setSnackbar({
        open: true,
        message: res.data.message || "Campaigns synced",
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["checkout-campaigns-cache"] });
      // The Order Entry dropdown reads the same cache — refresh it too.
      queryClient.invalidateQueries({ queryKey: ["order-entry-campaigns"] });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message || err?.message || "Failed to sync campaigns",
        severity: "error",
      });
    },
  });

  const campaigns = data?.campaigns ?? [];

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered: Campaign[] = q
      ? campaigns.filter((c: Campaign) => {
          const name = (c.campaignName || "").toLowerCase();
          const cat = (c.campaignCategoryName || "").toLowerCase();
          return (
            name.includes(q) ||
            cat.includes(q) ||
            String(c.campaignId).includes(q)
          );
        })
      : campaigns;
    return filtered.map((c: Campaign) => ({ id: c.campaignId, ...c }));
  }, [campaigns, search]);

  if (!isAdmin) {
    return (
      <Box>
        <Alert
          severity="error"
          action={
            <Typography
              component="span"
              sx={{ cursor: "pointer", fontWeight: 600 }}
              onClick={() => navigate("/")}
            >
              Go back
            </Typography>
          }
        >
          You don't have access to this page.
        </Alert>
      </Box>
    );
  }

  const columns: GridColDef[] = [
    { field: "campaignId", headerName: "ID", width: 100 },
    {
      field: "campaignName",
      headerName: "Campaign",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => params.value || "-",
    },
  ];

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Checkout Campaigns
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.total ?? 0} sales-entry campaigns cached
            {data?.lastSyncedAt
              ? ` · last synced ${dayjs(data.lastSyncedAt).format(
                  "MMM D, YYYY h:mm A"
                )}`
              : " · never synced"}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            syncMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SyncIcon />
            )
          }
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          {syncMutation.isPending ? "Syncing..." : "Sync now"}
        </Button>
      </Stack>

      <TextField
        size="small"
        label="Search campaigns"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 260, mb: 2 }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading || syncMutation.isPending}
        disableRowSelectionOnClick
        autoHeight
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: "background.paper",
          },
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
